import { IPersonalizedComponentVariant } from "../types/cache";
import {
  evalTrigger,
  getQueryString,
  optanonConsented,
  parsePzn,
  PznData
} from "./pzn-utils/pzn-utils";
import { logPageRequestDebug, logPageError, logPageInfo } from "../helper/logger";
import { userAgentFromString } from "next/server";

const ADAPTER_NAME = "resolvePersonalizedComponent";
const QA_DEVICE_TYPE_PARAM = "xDtvTestDeviceType";

type PersonalizedComponentInput = {
  key: string;
  variants: IPersonalizedComponentVariant[];
};
export function resolvePersonalizedComponent(
  data: PersonalizedComponentInput,
  options: {
    previousURL: string;
    cookieStore: any;
    queryParam: Record<string, string | string[]>;
    userAgent?: string;
  },
  page?: string,
  personalizationMapping?: any,
  pageSessionId?: string
) {
  try {
    const cookieList = [];
    logPageRequestDebug(ADAPTER_NAME, `Resolving Personalized Component ${  page || ""}`, {
      key: data?.key,
      variantsLength: data?.variants?.length || 0,
      options,
      pageSessionId
    });
    const { cookieStore, queryParam, previousURL } = options;

    const variants: IPersonalizedComponentVariant[] = data.variants.filter(
      variant => variant?.name !== "default"
    );

    let modData = null;

    function getCookieValue(key) {
      if (typeof cookieStore?.get === "function") {
        const cookie = cookieStore.get(key);
        return cookie ? cookie.value : "";
      }
      return cookieStore?.[key] || "";
    }

    // Sort triggers by priority (default, then highest num. first)
    const sortedVariants: IPersonalizedComponentVariant[] = [
      ...variants.sort(
        (a, b) => parseInt(b?.priority || "0", 10) - parseInt(a?.priority || "0", 10)
      )
    ];

    /**
     * @trigger If a DMA trigger's condition matches the current DMA value
     * @returns boolean */
    const checkDMA = trigger => {
      // The xDtvTestDMA query parameter is ONLY for QA testing purposes, should not be used by production
      const qaDMA = getQueryString(queryParam, "xDtvTestDMA");
      const dmaValue =
        qaDMA.length > 0 ? qaDMA : getCookieValue(trigger?.array_dynamicCookies?.[0]);
      return trigger?.condition?.items?.includes(dmaValue);
    };

    /**
     * @trigger If a return visitor trigger's condition matches is greater than user visit count
     * @returns boolean */
    const checkReturnVisitor = trigger => {
      // The testUserVisitCount query parameter is ONLY for QA testing purposes, should not be used by production
      const qaUvCount = getQueryString(queryParam, "testUserVisitCount");
      const uvCount =
        qaUvCount.length > 0 ? qaUvCount : getCookieValue(trigger?.array_dynamicCookies?.[0]);
      return (
        (!getCookieValue("visitedPCP") ||
          getCookieValue("visitedPCP") === "false") &&
        parseInt(uvCount) > parseInt(trigger?.condition?.items?.[0])
      );
    };

    /**
     * @trigger If UTM campaign qp exists, matches trigger's condition
     * @returns boolean */
    const checkUTM = trigger => {
      let utmCode = "";
      let conditionMet = false;

      if (queryParam) {
        for (const key in queryParam) {
          if (Object.prototype.hasOwnProperty.call(queryParam, key)) {
            const paramValue = String(queryParam[key] ?? '').toLowerCase();
            if (
              key.includes("utm") &&
              trigger?.condition?.items?.some(item => String(item ?? '').toLowerCase() === paramValue)
            ) {
              conditionMet = true;
              // Persist the normalized first value (always a string). The match
              // above may read a comma-joined array for a duplicate param;
              // storing the first element keeps `utcode` a clean string and
              // never pushes an array as a cookie value.
              utmCode = getQueryString(queryParam, key);
              break;
            }
          }
        }
      }

      if (!conditionMet && getCookieValue("utcode")?.length > 0) {
        const cookieValue = String(getCookieValue("utcode") ?? '').toLowerCase();
        conditionMet = trigger?.condition?.items?.some(
          item => String(item ?? '').toLowerCase() === cookieValue
        );
        utmCode = getCookieValue("utcode");
      }
      if(utmCode?.length > 0){
        cookieList.push({
          name: "utcode",
          value: utmCode
        });
      }

      return conditionMet;
    };

    /**
     * @trigger if a previous visited URL matches a trigger's condition
     * @returns boolean */
    const checkPrevVisited = trigger => {
      try {
        const parsedURL = new URL(previousURL);
        const pathAfterDomain = parsedURL.pathname + parsedURL.search;
        return trigger?.condition?.items?.includes(pathAfterDomain);
      } catch (error) {
        logPageError(ADAPTER_NAME, error, {
          step: "checkPrevVisited",
          page: page || "",
          pageSessionId
        });
        return false;
      }
    };

    /**
     * @trigger f a ffp qp exists, if it matches a FFP trigger's condition
     * @returns boolean */
    const checkFFP = trigger => {
      // The xDtvTestFFP query parameter is ONLY for QA testing purposes, should not be used by production
      const qaFFP = getQueryString(queryParam, "xDtvTestFFP");
      const ffp =
        qaFFP.length > 0 ? qaFFP : getCookieValue(trigger?.array_dynamicCookies?.[0]);
      return trigger?.condition?.items?.includes(ffp);
    };

    /**
     * Classifies the User-Agent into Mobile, Tablet, or Desktop using
     * Next.js's built-in UA parser (ua-parser-js). Handles CTV/SmartTV,
     * game consoles, and bot traffic correctly.
     * QA override: ?xDtvTestDeviceType=Mobile|Tablet|Desktop
     *
     * NOTE: iPadOS 13+ Safari sends a macOS-style UA string by default
     * ("Request Desktop Website" enabled). ua-parser-js cannot distinguish
     * these from a real Mac, so modern iPads classify as Desktop.
     * CMS authors should be aware that Tablet variants will NOT match
     * iPads in desktop-mode Safari. See WAT-10275 for details.
     */
    const checkDeviceType = (trigger: { condition?: { items?: string[] } }): boolean => {
      // QA override takes precedence
      const override = queryParam?.[QA_DEVICE_TYPE_PARAM];
      if (typeof override === "string" && override.length > 0) {
        return trigger?.condition?.items?.some(
          item => item?.toLowerCase() === override.toLowerCase()
        ) ?? false;
      }

      const ua = (options.userAgent || "").slice(0, 512); // length cap for safety
      const { device } = userAgentFromString(ua);

      // ua-parser-js device.type: "mobile" | "tablet" | "smarttv" | "console" | "wearable" | "embedded" | undefined
      // We only distinguish Mobile and Tablet; everything else (including CTV, bots, desktop) → Desktop
      let deviceType = "Desktop";
      if (device?.type === "mobile") {
        deviceType = "Mobile";
      } else if (device?.type === "tablet") {
        deviceType = "Tablet";
      }

      return trigger?.condition?.items?.some(
        item => item?.toLowerCase() === deviceType.toLowerCase()
      ) ?? false;
    };

    const checkPzn = (trigger): boolean => {
      // check optanon consent first
      if (optanonConsented(getCookieValue("OptanonConsent"))) {
        // NOTE: unlike evaluate-redirect, this component-swap engine does NOT
        // prod-gate the xDtvTestPzn QA override (consent is required first, so
        // it cannot bypass consent — it only previews a variant). Threading an
        // allowQAOverrides flag here is deferred (see PR notes).
        // getQueryString coerces array/duplicate params to a string so .trim()
        // below cannot throw.
        const qaPzn = getQueryString(queryParam, "xDtvTestPzn");
        const pzn =
          qaPzn.length > 0 ?
            qaPzn :
            getCookieValue(trigger?.array_dynamicCookies?.[0]);
        const codes = new Set<string>(pzn?.trim()?.split("-"));
        const parsedPzn: PznData = parsePzn(codes, personalizationMapping);
        const triggers = {
          ...trigger?.condition?.[0],
          ...(trigger?.condition?.[0]?.page && {
            page: Array.isArray(trigger?.condition?.[0]?.page) ?
              trigger?.condition?.[0]?.page?.[0] :
              trigger?.condition?.[0]?.page
          })
        };
        return evalTrigger(triggers, parsedPzn);
      }
      return false;
    };

    /**
     * Process multi-trigger conditions for a variant
     * @triggers all trigger conditions met
     * @returns boolean */
    const processMultiTriggers = multiTriggerConditions => multiTriggerConditions.every(trigger => {
      switch (trigger?.triggerCategory) {
        case "pzn":
          return checkPzn(trigger);
        case "dma":
          return checkDMA(trigger);
        case "returnVisitor":
          return checkReturnVisitor(trigger);
        case "utm":
          return checkUTM(trigger);
        case "prevVisited":
          return checkPrevVisited(trigger);
        case "ffp":
          return checkFFP(trigger);
        case "deviceType":
          return checkDeviceType(trigger);
        default:
          return false;
      }
    });

    // Check multi-trigger variant
    let isMultipleTriggersMet = false;
    if (sortedVariants[1]?.name === "multiTriggers") {
      const multiTriggerConditions = sortedVariants[1]?.array_conditions;
      isMultipleTriggersMet = processMultiTriggers(multiTriggerConditions);
    }

    // Process single-trigger variants
    if (!isMultipleTriggersMet) {
      sortedVariants.some(variant => {
        const conditions = variant?.array_conditions;
        if (conditions?.length > 0) {
          return conditions.some(condition => {
            // CHECK: personalizedData.push(condition);

            let isConditionTrue = false;
            switch (condition.trigger) {
              case "pzn":
                isConditionTrue = checkPzn(condition);
                break;
              case "dma":
                isConditionTrue = checkDMA(condition);
                break;
              case "returnVisitor":
                isConditionTrue = checkReturnVisitor(condition);
                break;
              case "utm":
                isConditionTrue = checkUTM(condition);
                break;
              case "prevVisited":
                isConditionTrue = checkPrevVisited(condition);
                break;
              case "ffp":
                isConditionTrue = checkFFP(condition);
                break;
              case "deviceType":
                isConditionTrue = checkDeviceType(condition);
                break;
            }

            if (isConditionTrue) {
              modData = variant?.components?.length > 0 ? variant.components[0] : null;
            }

            return isConditionTrue;
          });
        }
        return false;
      });
    }

    if(!modData) {
      // a trigger was met but the chosen variant had no components, we want to fire an analytics event for this scenario, and return null so that the default component will be rendered without any personalized data. If we returned the original component without firing an event, we would not be able to differentiate between the scenario where no triggers were met vs a trigger was met but had no components.
      logPageInfo(ADAPTER_NAME, "No matching variant found, falling back to default", {
        key: data?.key,
        variantsLength: data?.variants?.length,
        options,
        page: page || "",
        pageSessionId
      })

      return {
        component: null,
        analytics: {
          isPersonalizedComponent: true
        },
        cookieList
      };
    }

    return {
      component: {
        ...modData,
        props: {
          ...modData.props,
          analytics: {
            ...modData.props.analytics,
            isPersonalizedComponent: true
          }
        }
      },
      cookieList
    };
  } catch (error) {
    logPageError(ADAPTER_NAME, error, {
      key: data?.key,
      variantsLength: data?.variants?.length || 0,
      options,
      page: page || "",
      pageSessionId
    });
    return null;
  }
}

export default resolvePersonalizedComponent;
